import React, { useState, useCallback, useEffect, useRef } from 'react';
import styled from 'styled-components';
import ConfirmModal from '../Common/ConfirmModal';
import languages from 'Languages.js';
import SearchableDropdown from '../Common/SearchableDropdown';

import { Table, Th, Tr, Td, ErrorText, AddButton, Input, Icon, Checkbox } from '../Common/DataTable.Styles.js';


const isoLanguages = languages.getLanguagesList();

const LanguagesTable = ({ rows, setLanguages, setIsDirty, currentTranslations, setCurrentTranslations }) => {
    const [editingId, setEditingId] = useState(null);
    const [editValues, setEditValues] = useState({});
    const [isNew, setIsNew] = useState(false);
    const [error, setError] = useState('');

    const [modalOpen, setModalOpen] = useState(false);
    const [rowToDelete, setRowToDelete] = useState(null);


    const [modalInfo, setModalInfo] = useState(null);

    const editingRowRef = useRef(null);
    const dropdownContainerRef = useRef(null);

    const NEW_ROW_ID = 'new-language-temp-id';

    const startEditing = (row, newRow = false) => {
        setEditingId(row.code);
        setEditValues({ ...row });
        setIsNew(newRow);
        setError('');
    };

    const cancelEdit = () => {
        if (isNew) {
            setLanguages(rows.filter(r => r.code !== editingId));
        }
        setEditingId(null);
        setEditValues({});
        setIsNew(false);
        setError('');
    };

    const saveEdit = useCallback(() => {
        if (editingId === null) return;

        if (!editValues.code || editValues.code === NEW_ROW_ID) {
            cancelEdit();
            return;
        }

        const sanitizedValues = {
            ...editValues,
            name: editValues.name ? editValues.name.replace(/<[^>]*>/g, '').trim() : ''
        };

        if (!sanitizedValues.name) {
            setError('Display Name is required.');
            return;
        }

        const isDuplicate = rows.some(r => r.code === sanitizedValues.code && r.code !== editingId);
        if (isDuplicate) {
            setModalInfo({
                title: 'Duplicate ID',
                message: 'This language ID is already in use. Please select a different one.'
            });
            return;
        }

        const oldCode = editingId;
        const newCode = sanitizedValues.code;

        //Check if the language code changed
        if (oldCode && oldCode !== newCode && oldCode !== NEW_ROW_ID) {
            setCurrentTranslations(prevTranslations => {
                const newTranslations = JSON.parse(JSON.stringify(prevTranslations));

                for (const controlId in newTranslations) {
                    const control = newTranslations[controlId];
                    for (const propKey in control) {
                        const prop = control[propKey];

                        if (typeof prop === 'object' && prop !== null && prop[oldCode] !== undefined) {
                            prop[newCode] = prop[oldCode];
                            delete prop[oldCode];
                        }

                        if (propKey === 'columns' && typeof prop === 'object' && prop !== null) {
                            for (const columnId in prop) {
                                const column = prop[columnId];
                                for (const colPropKey in column) {
                                    const colProp = column[colPropKey];
                                    if (typeof colProp === 'object' && colProp !== null && colProp[oldCode] !== undefined) {
                                        colProp[newCode] = colProp[oldCode];
                                        delete colProp[oldCode];
                                    }
                                }
                            }
                        }
                    }
                }
                return newTranslations;
            });
        }

        let updatedRows;
        if (isNew) {
            updatedRows = rows.map(r => (r.code === NEW_ROW_ID ? sanitizedValues : r));
        } else {
            updatedRows = rows.map(r => (r.code === editingId ? sanitizedValues : r));
        }
        setLanguages(updatedRows);
        setIsDirty(true);

        // Reset editing state after a successful save
        setEditingId(null);
        setEditValues({});
        setIsNew(false);
        setError('');
    }, [editValues, rows, editingId, isNew, cancelEdit, setLanguages, setIsDirty, setCurrentTranslations]);


    const confirmDelete = (row) => {
        setRowToDelete(row);
        setModalOpen(true);
    };

    const handleDeleteConfirmed = () => {
        setIsDirty(true);
        setLanguages(rows.filter(r => r.code !== rowToDelete.code));
        setModalOpen(false);
        setRowToDelete(null);
    };


    const addLanguage = () => {
        if (isNew) return;

        setIsDirty(true);
        // Create an empty placeholder row
        const newRow = { code: NEW_ROW_ID, name: '', active: true };

        // Add it to the list and immediately enter edit mode for it
        setLanguages(prevRows => [...prevRows, newRow]);
        startEditing(newRow, true);
    };

    const handleLanguageIdChange = useCallback((selectedLanguage) => {
        setEditValues(prevEditValues => ({
            ...prevEditValues,
            code: selectedLanguage.id,
            name: selectedLanguage.name
        }));
    }, []);


    //--- handle "click outside" to save ---
    useEffect(() => {
        if (editingId === null) { return; }

        const handleDocumentMouseDown = (event) => {
            // Check if the click is inside the editing row.
            if (editingRowRef.current && editingRowRef.current.contains(event.target)) {
                return;
            }
            // Check if the click is inside the dropdown's options list
            if (dropdownContainerRef.current && dropdownContainerRef.current.contains(event.target)) {
                return;
            }

            // If the click was outside both, trigger the save.
            saveEdit();
        };

        document.addEventListener('mousedown', handleDocumentMouseDown);

        return () => {
            document.removeEventListener('mousedown', handleDocumentMouseDown);
        };
    }, [editingId, saveEdit]);

    // --- handle global key presses for Enter and Escape ---
    useEffect(() => {
        if (editingId === null) { return; }

        const handleGlobalKeyDown = (event) => {
            if (event.key === 'Enter') {
                event.preventDefault();
                saveEdit();
            } else if (event.key === 'Escape') {
                event.preventDefault();
                cancelEdit();
            }
        };

        document.addEventListener('keydown', handleGlobalKeyDown);
        return () => {
            document.removeEventListener('keydown', handleGlobalKeyDown);
        };
    }, [editingId, saveEdit, cancelEdit]);

    return (
        <div>
            <Table>
                <thead>
                    <Tr>
                        <Th>ID</Th>
                        <Th>Display Name</Th>
                        <Th center>Active</Th>
                        <Th></Th>
                    </Tr>
                </thead>
                <tbody>
                    {rows.map((row, index) => {
                        const isEditing = editingId === row.code;

                        return (
                            <Tr
                                key={row.code}
                                onDoubleClick={() => !isEditing && startEditing(row)}
                                $alternate={index % 2 === 1}
                                data-editing-id={isEditing ? row.code : undefined}
                                ref={isEditing ? editingRowRef : null}
                            >
                                <Td>
                                    {isEditing ? (
                                        <SearchableDropdown
                                            options={isoLanguages}
                                            value={editValues.code}
                                            onChange={handleLanguageIdChange}
                                            noItemsText='No languages found.'
                                            shouldFocus={true}
                                            containerRef={dropdownContainerRef}
                                            allowUnmatched={false}            
                                            onUnmatchedInput={() => {
                                                setModalInfo({
                                                    title: 'Invalid Code',
                                                    message: 'The entered code is not a valid language. Please select one from the list.'
                                                });
                                            }}
                                        />
                                    ) : row.code}
                                </Td>
                                <Td>
                                    {isEditing ? (
                                        <>
                                            <Input
                                                value={editValues.name}
                                                onChange={(e) => setEditValues({ ...editValues, name: e.target.value })}
                                            />
                                            {error && <ErrorText>{error}</ErrorText>}
                                        </>
                                    ) : row.name}
                                </Td>
                                <Td center>
                                    {isEditing ? (
                                        <Checkbox
                                            checked={editValues.active}
                                            onChange={(e) => setEditValues({ ...editValues, active: e.target.checked })}
                                        />
                                    ) : (
                                        <Checkbox checked={row.active} disabled />
                                    )}
                                </Td>
                                <Td center>
                                    {isEditing ? (
                                        <>
                                            <Icon className="pf-icon-cancel" onClick={cancelEdit} title="Cancel"></Icon>
                                            <Icon className="pf-icon-check" onClick={saveEdit} title="Save"></Icon>
                                        </>
                                    ) : (
                                        <>
                                            <Icon className="pf-delete" onClick={() => confirmDelete(row)} title="Delete Language"></Icon>
                                            <Icon className="pf-icon-edit" onClick={() => startEditing(row)} title="Edit Language"></Icon>
                                        </>
                                    )}
                                </Td>
                            </Tr>
                        );
                    })}
                </tbody>
            </Table>
            <AddButton onClick={addLanguage}>
                <Icon className="pf-add" title="Add New Language"></Icon>Add New Language
            </AddButton>
            {modalOpen && (
                <ConfirmModal
                    title="Delete Language"
                    onClose={() => setModalOpen(false)}
                    buttons={[
                        {
                            label: 'Cancel',
                            variant: 'default',
                            onClick: () => setModalOpen(false),
                            icon: ''
                        },
                        {
                            label: 'Delete',
                            variant: 'green',
                            onClick: handleDeleteConfirmed,
                            icon: ''
                        }
                    ]}
                >
                    <div>
                        Are you sure you want to delete <strong>{rowToDelete?.name}</strong>?<br /><br />
                        This will permanently remove all translations associated with this language. This action cannot be undone.
                    </div>

                </ConfirmModal>
            )}

            {modalInfo && (
                <ConfirmModal
                    title={modalInfo.title}
                    onClose={() => setModalInfo(null)}
                    buttons={[{ label: 'OK', variant: 'orange', onClick: () => setModalInfo(null) }]}
                >
                    <div>{modalInfo.message}</div>
                </ConfirmModal>
            )}
        </div>
    );
}

export default LanguagesTable;
